/**
 * F27 (Wave 2E) — admin WhatsApp templates registry page.
 *
 * Route: `/whatsapp/templates`
 * Permission: `whatsapp:read` (super_admin + support + finance + content_moderator).
 *   Write action (Sync now) additionally gated client-side on `whatsapp:write`
 *   (super_admin only); the server re-enforces.
 *
 * Layout mirrors F28 `/email-suppressions` + F23 `/commission-rates`:
 *   - Header (title + description) + "Sync now" CTA top-right when canWrite
 *   - Table of registry rows: Name, Language, Category, Status, Meta ID,
 *     Last synced, Rejection reason
 *   - Empty state when registry is empty (initial state pre-sync)
 *
 * Sync trigger flow: POST /admin/whatsapp/templates/sync → Toast → refetch.
 * Errors propagate to a Toast and the Banner stays empty (the registry
 * remains usable even when sync fails).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  EmptyState,
  Skeleton,
  Table,
  type TableColumn,
  useToast,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import type {
  WhatsAppTemplate,
  WhatsAppTemplateCategory,
  WhatsAppTemplateStatus,
} from '@bravemove/types';
import { listWhatsAppTemplates, syncWhatsAppTemplates } from '../../../api/whatsapp';
import { translateApiError } from '../../../auth/errors';
import { useAuth } from '../../../auth/useAuth';
import { PageHeader } from '../../../components/PageHeader';

function statusTone(status: WhatsAppTemplateStatus): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (status) {
    case 'APPROVED':
      return 'success';
    case 'PENDING':
    case 'IN_APPEAL':
      return 'warning';
    case 'REJECTED':
    case 'DISABLED':
      return 'danger';
    case 'PAUSED':
    default:
      return 'neutral';
  }
}

function categoryTone(category: WhatsAppTemplateCategory): 'info' | 'neutral' {
  switch (category) {
    case 'AUTHENTICATION':
    case 'UTILITY':
      return 'info';
    case 'MARKETING':
    default:
      return 'neutral';
  }
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

export function WhatsAppTemplatesPage() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const auth = useAuth();
  const canWrite = auth.adminUser?.permissions?.includes('whatsapp:write') ?? false;

  const [rows, setRows] = useState<readonly WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listWhatsAppTemplates();
      setRows(result.data);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList();
  }, [fetchList]);

  const onSyncClick = async () => {
    setSyncing(true);
    try {
      const result = await syncWhatsAppTemplates();
      toast.push({
        tone: 'success',
        title: t('admin:whatsapp.templates.sync_success_toast', {
          synced: result.synced,
          remote: result.remoteCount,
          defaultValue: `Synced ${result.synced} of ${result.remoteCount} templates from Meta.`,
        }),
      });
      void fetchList();
    } catch {
      toast.push({
        tone: 'danger',
        title: t('admin:whatsapp.templates.sync_error_toast', {
          defaultValue: 'Sync failed. Check the Meta Business Manager dashboard.',
        }),
      });
    } finally {
      setSyncing(false);
    }
  };

  const columns = useMemo<readonly TableColumn<WhatsAppTemplate>[]>(
    () => [
      {
        key: 'name',
        header: t('admin:whatsapp.templates.table.column_name', { defaultValue: 'Name' }),
        cell: (row) => (
          <span
            className="font-mono text-[12.5px] text-ink"
            data-testid={`whatsapp-template-name-${row.name}-${row.language}`}
          >
            {row.name}
          </span>
        ),
      },
      {
        key: 'language',
        header: t('admin:whatsapp.templates.table.column_language', { defaultValue: 'Language' }),
        cell: (row) => <span className="text-[12px] text-ink-2">{row.language}</span>,
      },
      {
        key: 'category',
        header: t('admin:whatsapp.templates.table.column_category', { defaultValue: 'Category' }),
        cell: (row) => (
          <Badge tone={categoryTone(row.category)} size="sm">
            {t(`admin:whatsapp.templates.category.${row.category}`, {
              defaultValue: row.category,
            })}
          </Badge>
        ),
      },
      {
        key: 'status',
        header: t('admin:whatsapp.templates.table.column_status', { defaultValue: 'Status' }),
        cell: (row) => (
          <Badge
            tone={statusTone(row.status)}
            size="sm"
            data-testid={`whatsapp-template-status-${row.name}-${row.language}`}
          >
            {t(`admin:whatsapp.templates.status.${row.status}`, { defaultValue: row.status })}
          </Badge>
        ),
      },
      {
        key: 'metaTemplateId',
        header: t('admin:whatsapp.templates.table.column_meta_id', { defaultValue: 'Meta ID' }),
        cell: (row) => (
          <span className="font-mono text-[11.5px] text-ink-3">{row.metaTemplateId ?? '—'}</span>
        ),
      },
      {
        key: 'lastSyncedAt',
        header: t('admin:whatsapp.templates.table.column_last_synced', {
          defaultValue: 'Last synced',
        }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">{formatDate(row.lastSyncedAt, locale)}</span>
        ),
      },
      {
        key: 'rejectionReason',
        header: t('admin:whatsapp.templates.table.column_rejection_reason', {
          defaultValue: 'Rejection reason',
        }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2 leading-snug">
            {row.rejectionReason ? truncate(row.rejectionReason, 80) : '—'}
          </span>
        ),
      },
    ],
    [t, locale],
  );

  return (
    <div className="space-y-6" data-testid="whatsapp-templates-page">
      <PageHeader
        title={t('admin:whatsapp.templates.page_title', { defaultValue: 'WhatsApp templates' })}
        subtitle={t('admin:whatsapp.templates.page_description', {
          defaultValue:
            'Meta-managed message templates. Approval lifecycle is canonical in Business Manager; this page reflects the daily sync. Manual sync is super-admin only and rate-limited (5/h).',
        })}
        actions={
          canWrite ? (
            <Button
              variant="primary"
              onClick={onSyncClick}
              loading={syncing}
              data-testid="whatsapp-templates-sync-button"
            >
              {t('admin:whatsapp.templates.sync_button', { defaultValue: 'Sync now' })}
            </Button>
          ) : null
        }
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="whatsapp-templates-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<UIIcon name="bell" size={36} color="currentColor" />}
          title={t('admin:whatsapp.templates.empty_title', {
            defaultValue: 'No WhatsApp templates registered yet',
          })}
          description={t('admin:whatsapp.templates.empty_description', {
            defaultValue:
              'Templates appear here after the daily sync worker tick OR a manual Sync. Submit templates via Meta Business Manager first.',
          })}
        />
      ) : (
        <Table
          data={rows as WhatsAppTemplate[]}
          columns={columns as TableColumn<WhatsAppTemplate>[]}
          rowKey={(row) => `${row.name}-${row.language}`}
        />
      )}
    </div>
  );
}

export default WhatsAppTemplatesPage;
